import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import { ContractedKmAlertDashboardDto } from 'api/dashboard/dtos/ContractedKmAlertDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import kpiStyles from '../kpiCards/KpiCards.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const formatKm = (value: number) => {
	return Math.round(value).toLocaleString('fr-FR') + ' km';
};

const MileageKpiIndicators = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<ContractedKmAlertDashboardDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: {};
				const result = await DashboardService.getContractedKmAlerts(criteria);
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters]);

	const items = data || [];
	const exceededCount = items.filter(x => x.percentageUsed >= 100).length;
	const totalExceededKm = items
		.filter(x => x.percentageUsed >= 100)
		.reduce((sum, x) => sum + Math.max(x.currentKilometers - x.contractedKilometers, 0), 0);
	const estimatedExcessCost = items
		.filter(x => x.percentageUsed >= 100)
		.reduce((sum, x) => {
			const excessKm = Math.max(x.currentKilometers - x.contractedKilometers, 0);
			const costRate = x.costPerKmOver ?? 0;
			return sum + excessKm * costRate;
		}, 0);

	if (isLoading) {
		return (
			<Box className={tableStyles.container} title={t('dashboard.mileage_kpi_indicators' as any) as string} subTitleTooltip={t('dashboard.mileage_kpi_indicators_tooltip' as any) as string}>
				<div className={kpiStyles.centerAlign}><LoadingSpinner /></div>
			</Box>
		);
	}

	return (
		<Box className={tableStyles.container} title={t('dashboard.mileage_kpi_indicators' as any) as string} subTitleTooltip={t('dashboard.mileage_kpi_indicators_tooltip' as any) as string}>
			<div className={kpiStyles.kpiRow}>
				<div className={kpiStyles.kpiItem}>
					<div className={kpiStyles.kpiInline}>
						<div className={kpiStyles.kpiLabel}>
							{t('dashboard.mileage_kpi.vehicles_exceeded' as any)}
						</div>
						<div className={`${kpiStyles.kpiValue}`} style={{ color: '#e04f5f' }}>
							{exceededCount}
						</div>
					</div>
				</div>
				<div className={kpiStyles.kpiItem}>
					<div className={kpiStyles.kpiInline}>
						<div className={kpiStyles.kpiLabel}>
							{t('dashboard.mileage_kpi.total_exceeded_km' as any)}
						</div>
						<div className={`${kpiStyles.kpiValue}`} style={{ color: '#e8a824' }}>
							{formatKm(totalExceededKm)}
						</div>
					</div>
				</div>
				<div className={kpiStyles.kpiItem}>
					<div className={kpiStyles.kpiInline}>
						<div className={kpiStyles.kpiLabel}>
							{t('dashboard.mileage_kpi.excess_cost' as any)}
						</div>
						<div className={`${kpiStyles.kpiValue}`} style={{ color: '#e04f5f' }}>
							<MoneyFormat value={estimatedExcessCost} fixedDecimalScale={true} decimalScale={2} />
						</div>
					</div>
				</div>
			</div>
		</Box>
	);
};

export default MileageKpiIndicators;
